import { Record as record, List } from 'immutable';

import { resolveAll } from '../helpers/LinkMapper';
import { TimeTypes, JourneyDirections } from '../constants/AppConstants';

import Service from './Service';
import Fares from './Fares';

const defaults = {
    outward: new List(),
    inward: new List()
};

function getOrderForDirection (direction, search) {
    const { arriveDepart } = search.getTimebandForDirection(direction);
    return arriveDepart === TimeTypes.DEPART ? 'origin' : 'destination';
}

function getOrderedForDirection (list, orderBy) {
    const timePath = ['journey', orderBy, 'time', 'scheduledTime'];
    return list.sort((a, b) =>
        a.getIn(timePath) - b.getIn(timePath) || a.getTripDuration() - b.getTripDuration()
    );
}

function getServiceFilter (trip) {
    return (service) => !service.isDisabledForTrip(trip);
}

function getSingleFaresFromServiceList (services, trip) {
    let fares = new List();
    services.filter(getServiceFilter(trip)).map((service)=> {
        fares = fares.concat(service.fares.getSingleFares(!trip.isReturn()));
    });

    return fares.toSet().toList().sortBy(fare => fare.getPrice());
}

function getReturnFaresFromServiceList (services, trip) {
    let fares = new List();
    services.filter(getServiceFilter(trip)).map((service)=> {
        fares = fares.concat(service.fares.getReturnFares());
    });

    return fares.toSet().toList().sortBy(fare => fare.getPrice());
}

class Services extends record(defaults) {

    static fromJSON (json) {
        return new Services({
            outward: resolveAll(json.outward).as(Service),
            inward: resolveAll(json.inward).as(Service)
        });
    }

    orderForSearch (search) {
        return this
            .set('outward', getOrderedForDirection(this.outward, getOrderForDirection(JourneyDirections.OUTWARD, search)))
            .set('inward', getOrderedForDirection(this.inward, getOrderForDirection(JourneyDirections.INWARD, search)));
    }

    getServicesForDirection (direction) {
        return this[direction];
    }

    getAllFares (trip) {
        let fares = this.getAllSingleFares(trip)
            .concat(this.getAllReturnFares(trip));

        if (!trip.filters.showStandard) {
            fares = fares.filterNot(Fares.isStandard);
        }

        if (!trip.filters.showFirstClass) {
            fares = fares.filterNot(Fares.isFirstClass);
        }

        return fares;
    }

    getAllSingleFares (trip) {
        return this.getAllSingleOutwardFares(trip)
            .concat(this.getAllSingleInwardFares(trip))
            .groupBy(fare => fare.id).toList().map(x => x.first())
            .sortBy(fare => fare.getPrice());
    }

    getAllReturnFares (trip) {
        return this.getAllReturnOutwardFares(trip)
            .concat(this.getAllReturnInwardFares(trip))
            .groupBy(fare => fare.id).toList().map(x => x.first())
            .sortBy(fare => fare.getPrice());
    }

    getAllSingleOutwardFares (trip) {
        return getSingleFaresFromServiceList(this.outward, trip);
    }

    getAllSingleInwardFares (trip) {
        return getSingleFaresFromServiceList(this.inward, trip);

    }

    getAllReturnOutwardFares (trip) {
        return getReturnFaresFromServiceList(this.outward, trip);
    }

    getAllReturnInwardFares (trip) {
        return getReturnFaresFromServiceList(this.inward, trip);
    }

    getLatestArrivingService (journeyDirection) {
        return this.getServicesForDirection(journeyDirection).last();
    }

    getEarliestLeavingService (journeyDirection) {
        return this.getServicesForDirection(journeyDirection).first();
    }

    getAllReturnTravelcards (trip) {
        return this.getAllReturnFares(trip).filter((item) => item.ticketType.isReturnTravelcard());
    }

    hasDisruptedServices () {
        const disruptedServices = this.outward.concat(this.inward)
            .filter((service) => service.isDisrupted());
        return !disruptedServices.isEmpty();
    }

    hasServices () {
        return !!this.outward.concat(this.inward).count();
    }


    hasReturnTravelcards(trip) {
        return !!this.getAllReturnTravelcards(trip).count();
    }

    hasCollectableServices (trip) {
        const collectableServices = this.outward.filter((service) => {
            return trip.isSmartcard() ? service.isCollectableSmartcard() : service.isCollectable();
        });
        return !!collectableServices.count();
    }
}


export default Services;



// WEBPACK FOOTER //
// ./src/records/Services.js